import {StringUtils} from "../../node_modules/igv-utils/src/index.js"
import Chromosome from "./chromosome.js"
import {loadSequence} from "./loadSequence.js"
import ChromAliasBB from "./chromAliasBB.js"
import ChromAliasFile from "./chromAliasFile.js"
import CytobandFileBB from "./cytobandFileBB.js"
import CytobandFile from "./cytobandFile.js"

import {loadChromSizes} from "./chromSizes.js"
import ChromAliasDefaults from "./chromAliasDefaults.js"
import {updateReference} from "./updateReference.js"
import BWSource from "../bigwig/bwSource.js"
import {loadFailure} from "../util/loadFailure.js"

const chromAliasWarning =
    "Chromosome aliases could not be loaded. Data files and searches that name chromosomes differently from this " +
    "genome (e.g. RefSeq accessions rather than chr1) may not match."

const ucsdIDMap = new Map([
    ["1kg_ref", "hg18"],
    ["1kg_v37", "hg19"],
    ["b37", "hg19"]
])

/**
 * The Genome class represents an assembly and consists of the following elements
 *   sequence - Object representing the DNA sequence
 *   chromosomes - Objects with chromosome meta data including name, length, and alternate names (aliases)
 *   aliases - table of chromosome name aliases (optional)
 *   cytobands - cytoband data for drawing an ideogram (optional)
 */

class Genome {

    #wgChromosomeNames
    #aliasRecordCache = new Map()
    #cytobandFailures = new Set()
    #chromAliasConfig

    // Optional parts that failed to load, as {kind, url, message, warning}: left out, and reported by the browser
    loadFailures = []

    // Set by the browser once it has taken the genome: called with each later load failure.  See #recordLoadFailure
    onLoadFailure

    static async createGenome(options, browser) {

        updateReference(options)
        const genome = new Genome(options, browser)
        await genome.init()
        return genome
    }

    constructor(config, browser) {
        this.config = config
        this.browser = browser
        this.id = config.id || generateGenomeID(config)
        this.ucscID = config.ucscID || ucsdIDMap.get(this.id) || this.id
        this.blatDB = config.blatDB || this.ucscID
        this.name = config.name
        this.nameSet = config.nameSet
    }


    async init() {

        const config = this.config

        // Load sequence.  This is required: the genome loads if and only if its sequence source loads
        this.sequence = await loadSequence(config, this.browser)
        this.loadFailures.push(...(this.sequence.loadFailures ?? []))

        // Load cytobands.  This is optional but required to support the ideogram.  Only needed for whole genome view
        if (false !== config.showIdeogram && false !== config.wholeGenomeView) {
            if (config.cytobandURL) {
                this.cytobandSource = new CytobandFile(config.cytobandURL, Object.assign({}, config))
            } else if (config.cytobandBbURL) {
                this.cytobandSource = new CytobandFileBB(config.cytobandBbURL, Object.assign({}, config), this)
            }
        }

        // Search for chromosomes, that is an array of chromosome objects containing name and length.  This is
        // optional but required to support whole genome view.
        if (this.sequence.chromosomes) {
            this.chromosomes = this.sequence.chromosomes
        } else if (config.chromSizesURL) {
            // Optional: if it fails, leave it out and load chromosomes from the sequence as they are needed.  Without
            // it there may be no chromosome to start on, which the browser checks before it uses the genome
            try {
                this.chromosomes = await loadChromSizes(config.chromSizesURL)
            } catch (error) {
                const lost = this.sequence.chromosomeNames ?
                    "whole-genome view will not be available" :
                    "whole-genome view and the chromosome list will not be available"
                this.#recordLoadFailure('chromSizes', config.chromSizesURL, error,
                    `Chromosome sizes could not be loaded, so ${lost}.`)
                this.chromosomes = new Map()
            }
        } else {
            this.chromosomes = new Map()   // Cache, chromosome are added as they are loaded
        }

        // Search for chromosome names.  This is optional but required to support the chromosome pulldown
        if (this.sequence.chromosomeNames) {
            this.chromosomeNames = this.sequence.chromosomeNames    // Twobit files can supply chromosome names unless they use an external index
        } else if (this.chromosomes.size > 0) {
            this.chromosomeNames = Array.from(this.chromosomes.keys())
        }

        // Chromosome alias
        this.#chromAliasConfig = config
        this.chromAlias = this.#createChromAlias(config)

        if (false !== config.wholeGenomeView && this.chromosomes.size > 0) {
            // Set chromosome order for WG view and chromosome pulldown.  If chromosome order is not specified sort
            if (config.chromosomeOrder) {
                if (Array.isArray(config.chromosomeOrder)) {
                    this.#wgChromosomeNames = config.chromosomeOrder
                } else {
                    this.#wgChromosomeNames = config.chromosomeOrder.split(',').map(nm => nm.trim())
                }
                // Trim to remove non-existent chromosomes
                await this.#useChromAlias(chromAlias => chromAlias.preload(this.#wgChromosomeNames))
                this.#wgChromosomeNames =
                    this.#wgChromosomeNames.map(c => this.getChromosomeName(c)).filter(c => this.chromosomes.has(c))
            } else {
                this.#wgChromosomeNames = trimSmallChromosomes(this.chromosomes)
                await this.#useChromAlias(chromAlias => chromAlias.preload(this.#wgChromosomeNames))
            }
        }

        // Optionally create the psuedo chromosome "all" to support whole genome view
        this.wholeGenomeView = config.wholeGenomeView !== false && this.#wgChromosomeNames && this.chromosomes.size > 1
        if (this.wholeGenomeView) {
            const l = this.#wgChromosomeNames.reduce((accumulator, currentValue) => accumulator + this.chromosomes.get(currentValue).bpLength, 0)
            this.chromosomes.set("all", new Chromosome("all", 0, l))
        }
    }

    // There is always an alias source, so code that uses one needn't check: the defaults know nothing without
    // chromosome names, but answer
    #createChromAlias(config) {
        if (config.chromAliasBbURL) {
            return new ChromAliasBB(config.chromAliasBbURL, Object.assign({}, config), this)
        } else if (config.aliasURL) {
            return new ChromAliasFile(config.aliasURL, Object.assign({}, config), this)
        } else {
            return new ChromAliasDefaults(this.id, this.chromosomeNames)
        }
    }

    /**
     * Call the chromosome alias source, falling back if it fails.  The alias bigBed and the alias file are optional
     * parts: if one fails, the failure is recorded, the next alias source replaces it (the alias file, else the
     * defaults), and the call is repeated on that.
     *
     * @param fn  Function of the alias source, returning a promise
     * @returns {Promise<*>}  Promise for the result of fn
     */
    async #useChromAlias(fn) {
        const chromAlias = this.chromAlias
        try {
            return await fn(chromAlias)
        } catch (error) {
            const failedURL = chromAlias instanceof ChromAliasBB ? 'chromAliasBbURL' :
                chromAlias instanceof ChromAliasFile ? 'aliasURL' : undefined
            if (!failedURL) {
                throw error   // The defaults load nothing, so this is not a load failure
            }
            if (this.chromAlias === chromAlias) {   // Concurrent calls can fail together; fall back once
                this.#recordLoadFailure('chromAlias', this.#chromAliasConfig[failedURL], error, chromAliasWarning)
                this.#chromAliasConfig = {...this.#chromAliasConfig, [failedURL]: undefined}
                this.chromAlias = this.#createChromAlias(this.#chromAliasConfig)
            }
            return this.#useChromAlias(fn)
        }
    }

    /**
     * Record that an optional part failed to load: log it, and either collect it in loadFailures, for the browser to
     * report with the rest of the load, or, once the browser has taken the genome and set onLoadFailure, pass it to
     * that, so a part that loads lazily (cytobands, aliases) is reported as it fails.
     *
     * @param kind  What failed, e.g. 'cytobands'
     * @param url  The resource that failed
     * @param error  The error it failed with
     * @param warning  What failed and its likely consequence, for the user
     */
    #recordLoadFailure(kind, url, error, warning) {
        console.error(error)
        const failure = loadFailure(kind, url, error, warning)
        if (this.onLoadFailure) {
            this.onLoadFailure(failure)
        } else {
            this.loadFailures.push(failure)
        }
    }

    get description() {
        return this.config.description || `${this.id}\n${this.name}`
    }

    get infoURL() {
        return this.config.infoURL
    }

    showWholeGenomeView() {
        return this.wholeGenomeView
    }

    /**
     * Return a json like object representing the current state.  The tracks collection is nullified
     * as tracks are transferred to the browser object on loading.
     *
     * @returns {any}
     */
    toJSON() {
        return Object.assign({}, this.config, {tracks: undefined})
    }

    get initialLocus() {
        return this.config.locus ? this.config.locus : this.getHomeChromosomeName()
    }

    getHomeChromosomeName() {
        if (this.showWholeGenomeView() && this.chromosomes.has("all")) {
            return "all"
        } else if (this.chromosomeNames) {
            return this.chromosomeNames[0]
        } else {
            // No list of names, e.g. a 2bit with an external index and no chrom.sizes: use the first sequence
            return this.sequence?.getFirstChromosomeName?.()
        }
    }

    getChromosomeName(chr) {
        return this.chromAlias ? this.chromAlias.getChromosomeName(chr, this.chromosomes.keys()) : chr
    }

    getChromosomeDisplayName(str) {
        if (this.nameSet && this.chromAlias) {
            return this.chromAlias.getChromosomeAlias(str, this.nameSet) || str
        } else {
            return str
        }
    }

    getChromosome(chr) {
        if (this.chromAlias) {
            chr = this.chromAlias.getChromosomeName(chr)
        }
        return this.chromosomes.get(chr)
    }

    async loadChromosome(chr) {

        const chromAliasRecord = await this.getAliasRecord(chr)
        if (chromAliasRecord) {
            chr = chromAliasRecord.chr
        }

        if (!this.chromosomes.has(chr)) {
            let chromosome
            const sequenceRecord = await this.sequence.getSequenceRecord(chr)
            if (sequenceRecord) {
                chromosome = new Chromosome(chr, 0, sequenceRecord.bpLength)
            }

            this.chromosomes.set(chr, chromosome)  // <= chromosome might be undefined, setting it prevents future attempts
        }

        return this.chromosomes.get(chr)
    }

    async getAliasRecord(chr) {
        if (this.#aliasRecordCache.has(chr)) {
            return this.#aliasRecordCache.get(chr)
        }
        if (this.chromAlias) {
            let aliasRecord = await this.#useChromAlias(chromAlias => chromAlias.search(chr))
            if (!aliasRecord && chr !== chr.toLowerCase()) {
                aliasRecord = await this.#useChromAlias(chromAlias => chromAlias.search(chr.toLowerCase()))
            }
            if (aliasRecord) {
                // Add some aliases for case insensitivy
                const upper = aliasRecord.chr.toUpperCase()
                const lower = aliasRecord.chr.toLowerCase()
                const cap = aliasRecord.chr.charAt(0).toUpperCase() + aliasRecord.chr.slice(1)
                if (aliasRecord.chr !== upper) {
                    aliasRecord["_uppercase"] = upper
                }
                if (aliasRecord.chr !== lower) {
                    aliasRecord["_lowercase"] = lower
                }
                if (aliasRecord.chr !== cap) {
                    aliasRecord["_cap"] = cap
                }
            }
            this.#aliasRecordCache.set(chr, aliasRecord)  // Set even if undefined to prevent recurrent searches
            return aliasRecord
        }
    }

    /**
     * Return the cytobands for a chromosome.  The cytobands load lazily, on first use, so a failure is an optional
     * part failing after the load: that chromosome's ideogram is drawn without them, other chromosomes still load
     * theirs, and the user is warned once.
     */
    async getCytobands(chr) {
        if (this.cytobandSource) {
            const chrName = this.getChromosomeName(chr)
            if (this.#cytobandFailures.has(chrName)) {
                return
            }
            try {
                return await this.cytobandSource.getCytobands(chrName)
            } catch (error) {
                if (!this.#cytobandFailures.has(chrName)) {    // Concurrent requests can fail together; log once
                    this.#cytobandFailures.add(chrName)
                    if (1 === this.#cytobandFailures.size) {     // Warn for the first chromosome only
                        this.#recordLoadFailure('cytobands', this.config.cytobandURL || this.config.cytobandBbURL,
                            error, "Cytobands could not be loaded, so chromosome ideograms will be drawn without bands.")
                    } else {
                        console.error(error)
                    }
                }
            }
        }
    }

    getChromosomes() {
        return this.chromosomes
    }

    get wgChromosomeNames() {
        return this.#wgChromosomeNames ? this.#wgChromosomeNames.slice() : undefined
    }

    get showChromosomeWidget() {
        return this.config.showChromosomeWidget
    }

    /**
     * Return the genome coordinate in kb for the give chromosome and position.
     * NOTE: This might return undefined if the chr is filtered from whole genome view.
     */
    getGenomeCoordinate(chr, bp) {

        var offset = this.getCumulativeOffset(chr)
        if (offset === undefined) return undefined

        return offset + bp
    }

    /**
     * Return the chromosome and coordinate in bp for the given genome coordinate
     */
    getChromosomeCoordinate(genomeCoordinate) {

        if (this.cumulativeOffsets === undefined) {
            this.cumulativeOffsets = this.#computeCumulativeOffsets()
        }

        let lastChr = undefined
        let lastCoord = 0
        for (let name of this.#wgChromosomeNames) {

            const cumulativeOffset = this.cumulativeOffsets[name]
            if (cumulativeOffset > genomeCoordinate) {
                const position = genomeCoordinate - lastCoord
                return {chr: lastChr, position: position}
            }
            lastChr = name
            lastCoord = cumulativeOffset
        }

        // If we get here off the end
        return {chr: this.#wgChromosomeNames[this.#wgChromosomeNames.length - 1], position: 0}

    };


    /**
     * Return the offset in genome coordinates (kb) of the start of the given chromosome
     * NOTE:  This might return undefined if the chromosome is filtered from whole genome view.
     */
    getCumulativeOffset(chr) {

        if (this.cumulativeOffsets === undefined) {
            this.cumulativeOffsets = this.#computeCumulativeOffsets()
        }

        const queryChr = this.getChromosomeName(chr)
        return this.cumulativeOffsets[queryChr]
    }

    #computeCumulativeOffsets() {

        let acc = {}
        let offset = 0
        for (let name of this.#wgChromosomeNames) {
            acc[name] = Math.floor(offset)
            const chromosome = this.getChromosome(name)
            offset += chromosome.bpLength
        }

        return acc
    }

    /**
     * Return the nominal genome length, this is the length of the main chromosomes (no scaffolds, etc).
     */
    getGenomeLength() {

        if (!this.bpLength) {
            let bpLength = 0
            for (let cname of this.#wgChromosomeNames) {
                let c = this.chromosomes.get(cname)
                bpLength += c.bpLength
            }
            this.bpLength = bpLength
        }
        return this.bpLength
    }

    async getSequence(chr, start, end) {
        chr = this.getChromosomeName(chr)
        return this.sequence.getSequence(chr, start, end)
    }

    /**
     * Return loaded sequence (i.e. cached or otherwise loaded) spanning the given region.  If no sequence has been
     * loaded returns undefined.
     *
     * @param chr
     * @param start
     * @param end
     */
    getSequenceInterval(chr, start, end) {
        if (typeof this.sequence.getSequenceInterval === 'function') {
            return this.sequence.getSequenceInterval(chr, start, end)
        } else {
            return undefined
        }
    }

    getHubURLs() {
        return this.config.hubs
    }

    /**
     * Return the Mane transcript with the given name, or null if not found. We also check the refseq historical
     * db if available for backward compatibility. This is only available for hg38.
     * @param {string} name - The name of the Mane transcript to search for.
     * @return {Promise<Object|null>} A Promise resolving to the Mane transcript object if found, or null otherwise.
     */
    async getManeTranscript(name) {

        if (!this.maneFeatureSource && this.config.maneBbURL) {
            this.loadManeFeatureSource()
        }
        if (this.maneFeatureSource) {
            const feature = await this.maneFeatureSource.search(name)
            if (feature) {
                return feature
            }
        }
        if (!this.rsDBFeatureSource && this.config.rsdbURL) {
            this.rsDBFeatureSource = new BWSource({url: this.config.rsdbURL}, this)
        }
        if (this.rsDBFeatureSource) {
            const feature = await this.rsDBFeatureSource.search(name)
            if (feature) {
                return feature
            }
        }
        return null
    }

    /**
     * Return the Mane transcript overlapping the given position, or null if none found.
     *
     * @param chr      Chromosome name (e.g., "chr1", "chrX") in which to search for the transcript.
     * @param position Genomic position (0-based coordinate) to check for overlap with a Mane transcript.
     * @return {Promise<*|null>} The feature representing the Mane transcript overlapping the specified position, or null if none is found.
     */
    async getManeTranscriptAt(chr, position) {
        if (!this.maneFeatureSource && this.config.maneBbURL) {
            this.loadManeFeatureSource()
        }
        if (this.maneFeatureSource) {
            try {
                const start = position
                const end = position + 1
                const features = await this.maneFeatureSource.getFeatures({chr, start, end})
                if (features) {
                    for (const feature of features) {
                        if (feature.start <= position && feature.end >= position) {
                            return feature
                        }
                    }
                }
            } catch (e) {
                console.error("Error fetching MANE transcript", e)
            }
        }
        return null
    }

    loadManeFeatureSource() {
        if (this.config.maneBbURL != null) {
            const bbConfig = {url: this.config.maneBbURL}
            if (this.config.maneTrixURL) {
                bbConfig.trixURL = this.config.maneTrixURL
            }
            this.maneFeatureSource = new BWSource(bbConfig, this)
        }
    }
}

/**
 * Trim small sequences (chromosomes) and return the list of trimmed chromosome names.
 * The results are used to construct the whole genome view and optionally chromosome pulldown
 * *
 * @param config - the "reference" configuration object
 * @returns {string|*|*[]|string[]}
 */
function trimSmallChromosomes(chromosomes) {

    const wgChromosomeNames = []
    let runningAverage
    let i = 1
    for (let c of chromosomes.values()) {
        if (!runningAverage) {
            runningAverage = c.bpLength
            wgChromosomeNames.push(c.name)
        } else {
            if (c.bpLength < runningAverage / 100) {
                continue
            }
            runningAverage = ((i - 1) * runningAverage + c.bpLength) / i
            wgChromosomeNames.push(c.name)
        }
        i++
    }
    return wgChromosomeNames
}

function isDigit(val) {
    return /^\d+$/.test(val)
}

function generateGenomeID(config) {
    if (config.id !== undefined) {
        return config.id
    }
    // twoBitURL first, the order loadSequence reads them in
    for (const url of [config.twoBitURL, config.fastaURL]) {
        if (!url) continue
        if (StringUtils.isString(url) && !url.startsWith("data:")) {
            return url
        } else if (url.name) {
            return url.name
        }
    }
    return ""
}

export default Genome
